import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getProducts, deleteProduct } from './services/firebaseService';
import Header from './Header';
import Footer from './Footer';
import AdminSidebar from './AdminSidebar';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

const PAGE_SIZE = 20;

const ManageProducts = () => {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const getCategoryLabel = (category) => (category === 'Others' ? 'Promotions' : category);

  const formatCurrency = (amount) => {
    const value = Number(amount);
    if (Number.isNaN(value)) return '—';
    return new Intl.NumberFormat('en-MY', {
      style: 'currency',
      currency: 'MYR'
    }).format(value);
  };

  const getCategoryBadgeClasses = (category) => {
    const badges = {
      'Power Tools': 'bg-blue-100 text-primary',
      'Welding Machines': 'bg-indigo-100 text-indigo-900',
      Accessories: 'bg-gray-200 text-gray-800',
      Generator: 'bg-amber-100 text-amber-900',
      'Air Compressors': 'bg-cyan-100 text-cyan-900',
      'Home Improvements': 'bg-teal-100 text-teal-900',
      Others: 'bg-purple-100 text-purple-900',
      'Machinery Service Consultant': 'bg-slate-200 text-slate-800'
    };

    return badges[category] || 'bg-gray-100 text-gray-800';
  };

  const totalPages = Math.max(1, Math.ceil(products.length / PAGE_SIZE));
  const startIndex = (currentPage - 1) * PAGE_SIZE;
  const endIndex = Math.min(startIndex + PAGE_SIZE, products.length);
  const paginatedProducts = products.slice(startIndex, endIndex);

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  // Load products from Firebase
  useEffect(() => {
    loadProducts();
  }, []);

  const loadProducts = async () => {
    setLoading(true);
    const result = await getProducts();
    if (result.success) {
      setProducts(result.products);
    } else {
      setError('Failed to load products: ' + result.error);
    }
    setLoading(false);
  };

  const handleEdit = (product) => {
    navigate(`/admin-portal/edit/${product.id}`);
  };

  const handleAdd = () => {
    navigate('/admin-portal/add');
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this product?')) {
      setLoading(true);
      const result = await deleteProduct(id);
      if (result.success) {
        await loadProducts(); // Reload products
        alert('Product deleted successfully!');
      } else {
        alert('Failed to delete product: ' + result.error);
      }
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <Header />
      <div className="pt-20 flex flex-1">
        <AdminSidebar activeItem="products" />
        <div className="flex-1 min-w-0 overflow-y-auto bg-gray-50 p-4 md:p-6">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-6 gap-4 border-b-2 border-gray-200 pb-4">
            <div>
              <button
                onClick={() => navigate('/admin-portal')}
                className="flex items-center gap-1 text-gray-500 hover:text-primary transition-colors mb-2 text-sm"
              >
                <ArrowBackIcon className="!w-[18px] !h-[18px]" />
                Back to Dashboard
              </button>
              <h1 className="text-3xl md:text-4xl font-bold text-gray-900">Product Inventory</h1>
            </div>
            <button
              onClick={handleAdd}
              className="flex items-center gap-2 bg-gray-200 text-primary py-2 px-4 rounded font-bold hover:opacity-90 transition-opacity self-start md:self-auto shadow-md hover:shadow-none hover:translate-y-px disabled:opacity-60"
              disabled={loading}
            >
              <AddCircleOutlineIcon className="!w-5 !h-5" />
              Add Product
            </button>
          </div>

          {error && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
              {error}
            </div>
          )}

          <div className="bg-white border border-gray-200 rounded shadow-sm overflow-x-auto">
            {loading && (
              <div className="p-8 text-center">
                <div className="text-gray-500">Loading products...</div>
              </div>
            )}
            {!loading && products.length === 0 && (
              <div className="p-8 text-center">
                <div className="text-gray-500">No products found. Add your first product!</div>
              </div>
            )}
            {!loading && products.length > 0 && (
              <>
                <table className="w-full text-left border-collapse min-w-[900px]">
                  <thead>
                    <tr className="bg-gray-100 border-b-2 border-gray-200">
                      <th className="py-2 px-4 text-xs uppercase tracking-wider font-semibold text-gray-900">ID</th>
                      <th className="py-2 px-4 text-xs uppercase tracking-wider font-semibold text-gray-900">SKU</th>
                      <th className="py-2 px-4 text-xs uppercase tracking-wider font-semibold text-gray-900">Product Name</th>
                      <th className="py-2 px-4 text-xs uppercase tracking-wider font-semibold text-gray-900">Category</th>
                      <th className="py-2 px-4 text-xs uppercase tracking-wider font-semibold text-gray-900 text-right">Normal Price</th>
                      <th className="py-2 px-4 text-xs uppercase tracking-wider font-semibold text-gray-900 text-right">Selling Price</th>
                      <th className="py-2 px-4 text-xs uppercase tracking-wider font-semibold text-gray-900 text-center">Status</th>
                      <th className="py-2 px-4 text-xs uppercase tracking-wider font-semibold text-gray-900 text-center">Action</th>
                    </tr>
                  </thead>
                  <tbody className="text-sm text-gray-900">
                    {paginatedProducts.map((product, index) => {
                      const normalPrice = Number(product.normalPrice);
                      const sellingPrice = Number(product.price);
                      const hasDiscount = !Number.isNaN(normalPrice) && !Number.isNaN(sellingPrice) && normalPrice > sellingPrice;

                      return (
                        <tr
                          key={product.id}
                          className={`border-b border-gray-200 transition-colors hover:bg-gray-100 ${
                            index % 2 === 1 ? 'bg-gray-50 hover:bg-gray-100' : ''
                          }`}
                        >
                          <td className="py-2 px-4 text-gray-500">{product.id}</td>
                          <td className="py-2 px-4 font-mono">{product.sku || 'N/A'}</td>
                          <td className="py-2 px-4 font-bold max-w-xs truncate">{product.name}</td>
                          <td className="py-2 px-4">
                            <span
                              className={`inline-flex items-center px-2 py-1 text-[10px] uppercase tracking-wider font-semibold rounded whitespace-nowrap ${getCategoryBadgeClasses(product.category)}`}
                            >
                              {getCategoryLabel(product.category)}
                            </span>
                          </td>
                          <td className={`py-2 px-4 text-right text-gray-500 ${hasDiscount ? 'line-through' : ''}`}>
                            {product.normalPrice ? formatCurrency(product.normalPrice) : '—'}
                          </td>
                          <td className={`py-2 px-4 text-right font-bold ${hasDiscount ? 'text-primary' : ''}`}>
                            {product.price ? formatCurrency(product.price) : '—'}
                          </td>
                          <td className="py-2 px-4 text-center">
                            {product.status === 'disable' ? (
                              <span className="inline-flex items-center px-2 py-1 text-[10px] uppercase tracking-wider font-semibold rounded whitespace-nowrap bg-red-100 text-red-800">
                                Disabled
                              </span>
                            ) : (
                              <span className="inline-flex items-center px-2 py-1 text-[10px] uppercase tracking-wider font-semibold rounded whitespace-nowrap bg-green-100 text-green-800">
                                Enabled
                              </span>
                            )}
                          </td>
                          <td className="py-2 px-4 text-center">
                            <div className="flex items-center justify-center gap-1">
                              <button
                                onClick={() => handleEdit(product)}
                                className="text-gray-400 hover:text-primary transition-colors p-1"
                                title="Edit"
                                disabled={loading}
                              >
                                <EditIcon className="!w-5 !h-5" />
                              </button>
                              <button
                                onClick={() => handleDelete(product.id)}
                                className="text-gray-400 hover:text-red-600 transition-colors p-1"
                                title="Delete"
                                disabled={loading}
                              >
                                <DeleteIcon className="!w-5 !h-5" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>

                <div className="bg-white border-t border-gray-200 p-2 flex items-center justify-between">
                  <span className="text-sm text-gray-500 pl-2">
                    Showing {startIndex + 1} to {endIndex} of {products.length} entries
                  </span>
                  <div className="flex items-center gap-1 pr-2">
                    <button
                      className="p-1 text-gray-400 hover:text-primary disabled:opacity-50"
                      disabled={currentPage === 1}
                      onClick={() => setCurrentPage((page) => page - 1)}
                      type="button"
                      aria-label="Previous page"
                    >
                      <ChevronLeftIcon className="!w-5 !h-5" />
                    </button>
                    {Array.from({ length: totalPages }, (_, index) => index + 1).map((page) => (
                      <button
                        key={page}
                        className={`w-8 h-8 rounded text-sm font-semibold flex items-center justify-center ${
                          page === currentPage
                            ? 'bg-primary text-white'
                            : 'text-gray-900 hover:bg-gray-100'
                        }`}
                        onClick={() => setCurrentPage(page)}
                        type="button"
                      >
                        {page}
                      </button>
                    ))}
                    <button
                      className="p-1 text-gray-400 hover:text-primary disabled:opacity-50"
                      disabled={currentPage === totalPages}
                      onClick={() => setCurrentPage((page) => page + 1)}
                      type="button"
                      aria-label="Next page"
                    >
                      <ChevronRightIcon className="!w-5 !h-5" />
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default ManageProducts;

